import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  Chip,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Menu,
  Divider,
  Stack,
} from '@mui/material';
import {
  Add,
  Search,
  Download,
  MoreVert,
  Error,
  CheckCircle,
  Schedule,
  Cancel,
  CalendarToday,
  LocalOffer,
  Close,
  FilterList,
} from '@mui/icons-material';
import { mockBugs, statusOptions, priorityOptions, Bug } from './bugTrackerMockData';
import './BugTracker.scss';

const BugTracker: React.FC = () => {
  const [bugs, setBugs] = useState<Bug[]>(mockBugs);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedPriority, setSelectedPriority] = useState<string>('all');
  const [selectedBug, setSelectedBug] = useState<Bug | null>(null);
  const [showBugDialog, setShowBugDialog] = useState(false);
  const [showNewBugDialog, setShowNewBugDialog] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [currentBugId, setCurrentBugId] = useState<string | null>(null);

  // New bug form state
  const [newBug, setNewBug] = useState({
    title: '',
    description: '',
    priority: 'medium' as const,
    assignee: '',
    dueDate: '',
    tags: '',
  });

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, bugId: string) => {
    setAnchorEl(event.currentTarget);
    setCurrentBugId(bugId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setCurrentBugId(null);
  };

  const handleViewDetails = () => {
    const bug = bugs.find((b) => b.id === currentBugId);
    if (bug) {
      setSelectedBug(bug);
      setShowBugDialog(true);
    }
    handleMenuClose();
  };

  const handleExport = () => {
    const csvContent = [
      ['ID', 'Title', 'Status', 'Priority', 'Assignee', 'Due Date', 'Tags'].join(','),
      ...filteredBugs.map((bug) =>
        [
          bug.id,
          `"${bug.title}"`,
          bug.status,
          bug.priority,
          bug.assignee.name,
          bug.dueDate,
          `"${bug.tags.join(', ')}"`,
        ].join(',')
      ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bugs-export.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleCreateBug = () => {
    const bugId = `BUG-${String(bugs.length + 1).padStart(3, '0')}`;
    const newBugData: Bug = {
      id: bugId,
      title: newBug.title,
      description: newBug.description,
      status: 'open',
      priority: newBug.priority,
      assignee: {
        id: 'u-new',
        name: newBug.assignee || 'Unassigned',
        initials: newBug.assignee
          ? newBug.assignee
              .split(' ')
              .map((n) => n[0])
              .join('')
          : 'UN',
      },
      reporter: {
        id: 'current-user',
        name: 'Current User',
      },
      dueDate: newBug.dueDate,
      createdDate: new Date().toISOString().split('T')[0],
      tags: newBug.tags.split(',').map((t) => t.trim()).filter((t) => t),
      comments: 0,
      attachments: 0,
    };

    setBugs([newBugData, ...bugs]);
    setShowNewBugDialog(false);
    setNewBug({
      title: '',
      description: '',
      priority: 'medium',
      assignee: '',
      dueDate: '',
      tags: '',
    });
  };

  const filteredBugs = bugs.filter((bug) => {
    const matchesSearch =
      bug.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      bug.id.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = selectedStatus === 'all' || bug.status === selectedStatus;
    const matchesPriority = selectedPriority === 'all' || bug.priority === selectedPriority;
    return matchesSearch && matchesStatus && matchesPriority;
  });

  const stats = {
    total: bugs.length,
    open: bugs.filter((b) => b.status === 'open').length,
    inProgress: bugs.filter((b) => b.status === 'in-progress').length,
    resolved: bugs.filter((b) => b.status === 'resolved').length,
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'open':
        return <Error sx={{ fontSize: 20 }} />;
      case 'in-progress':
        return <Schedule sx={{ fontSize: 20 }} />;
      case 'resolved':
        return <CheckCircle sx={{ fontSize: 20 }} />;
      case 'closed':
        return <Cancel sx={{ fontSize: 20 }} />;
      default:
        return <Error sx={{ fontSize: 20 }} />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return { bg: '#FEF2F2', color: '#DC2626', border: '#FCA5A5' };
      case 'in-progress':
        return { bg: '#FEF3C7', color: '#D97706', border: '#FCD34D' };
      case 'resolved':
        return { bg: '#D1FAE5', color: '#059669', border: '#6EE7B7' };
      case 'closed':
        return { bg: '#F3F4F6', color: '#6B7280', border: '#D1D5DB' };
      default:
        return { bg: '#F3F4F6', color: '#6B7280', border: '#D1D5DB' };
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical':
        return { bg: '#DC2626', color: '#FFFFFF' };
      case 'high':
        return { bg: '#F59E0B', color: '#FFFFFF' };
      case 'medium':
        return { bg: '#10B981', color: '#FFFFFF' };
      case 'low':
        return { bg: '#6B7280', color: '#FFFFFF' };
      default:
        return { bg: '#6B7280', color: '#FFFFFF' };
    }
  };

  return (
    <Box className="bug-tracker-page">
      {/* Header */}
      <Box className="bug-tracker-header">
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#2C3E50', mb: 0.5 }}>
            Bug Tracker
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Track and manage all reported bugs
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={handleExport}
            sx={{ textTransform: 'none' }}
          >
            Export
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setShowNewBugDialog(true)}
            sx={{ textTransform: 'none' }}
          >
            New Bug
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Box className="bug-stats">
        <Card className="stat-card stat-total">
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box className="stat-icon-wrapper" sx={{ bgcolor: '#EEF2FF' }}>
                <LocalOffer sx={{ color: '#4F46E5', fontSize: 24 }} />
              </Box>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#2C3E50' }}>
                  {stats.total}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Bugs
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card className="stat-card stat-open">
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box className="stat-icon-wrapper" sx={{ bgcolor: '#FEF2F2' }}>
                <Error sx={{ color: '#DC2626', fontSize: 24 }} />
              </Box>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#2C3E50' }}>
                  {stats.open}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Open
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card className="stat-card stat-in-progress">
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box className="stat-icon-wrapper" sx={{ bgcolor: '#FEF3C7' }}>
                <Schedule sx={{ color: '#D97706', fontSize: 24 }} />
              </Box>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#2C3E50' }}>
                  {stats.inProgress}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  In Progress
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card className="stat-card stat-resolved">
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box className="stat-icon-wrapper" sx={{ bgcolor: '#D1FAE5' }}>
                <CheckCircle sx={{ color: '#059669', fontSize: 24 }} />
              </Box>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#2C3E50' }}>
                  {stats.resolved}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Resolved
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Filters */}
      <Box className="bug-filters">
        <TextField
          placeholder="Search bugs by title or ID..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          sx={{ flex: 1, maxWidth: 400 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: '#9CA3AF' }} />
              </InputAdornment>
            ),
          }}
        />
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <Select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              startAdornment={
                <InputAdornment position="start">
                  <FilterList sx={{ fontSize: 18, ml: 0.5 }} />
                </InputAdornment>
              }
            >
              {statusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <Select
              value={selectedPriority}
              onChange={(e) => setSelectedPriority(e.target.value)}
            >
              {priorityOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Bug Table */}
      <TableContainer component={Paper} className="bug-table-container">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', fontSize: '0.75rem' }}>
                ID
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', fontSize: '0.75rem' }}>
                Title
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', fontSize: '0.75rem' }}>
                Status
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', fontSize: '0.75rem' }}>
                Priority
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', fontSize: '0.75rem' }}>
                Assignee
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', fontSize: '0.75rem' }}>
                Due Date
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', fontSize: '0.75rem' }}>
                Tags
              </TableCell>
              <TableCell
                align="center"
                sx={{ fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', fontSize: '0.75rem' }}
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredBugs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                  <Error sx={{ fontSize: 48, color: '#9CA3AF', mb: 2 }} />
                  <Typography color="text.secondary">No bugs found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredBugs.map((bug) => {
                const statusStyle = getStatusColor(bug.status);
                const priorityStyle = getPriorityColor(bug.priority);

                return (
                  <TableRow key={bug.id} hover sx={{ '&:hover': { bgcolor: '#F9FAFB' } }}>
                    <TableCell>
                      <Typography
                        sx={{
                          color: '#2563EB',
                          fontWeight: 600,
                          fontSize: '0.875rem',
                        }}
                      >
                        {bug.id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', mb: 0.5 }}>
                          {bug.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {bug.description}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(bug.status)}
                        label={bug.status.replace('-', ' ')}
                        size="small"
                        sx={{
                          bgcolor: statusStyle.bg,
                          color: statusStyle.color,
                          border: `1px solid ${statusStyle.border}`,
                          fontWeight: 500,
                          textTransform: 'capitalize',
                          '& .MuiChip-icon': {
                            color: statusStyle.color,
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={bug.priority.toUpperCase()}
                        size="small"
                        sx={{
                          bgcolor: priorityStyle.bg,
                          color: priorityStyle.color,
                          fontWeight: 600,
                          fontSize: '0.75rem',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor: '#2563EB',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                          }}
                        >
                          {bug.assignee.initials}
                        </Avatar>
                        <Typography variant="body2">{bug.assignee.name}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CalendarToday sx={{ fontSize: 16, color: '#9CA3AF' }} />
                        <Typography variant="body2">
                          {new Date(bug.dueDate).toLocaleDateString()}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {bug.tags.map((tag, index) => (
                          <Chip
                            key={index}
                            label={tag}
                            size="small"
                            sx={{
                              bgcolor: '#EEF2FF',
                              color: '#4F46E5',
                              fontSize: '0.75rem',
                              height: 24,
                            }}
                          />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, bug.id)}
                      >
                        <MoreVert />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Action Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleViewDetails}>View Details</MenuItem>
        <MenuItem onClick={handleMenuClose}>Edit</MenuItem>
        <MenuItem onClick={handleMenuClose}>Change Status</MenuItem>
        <Divider />
        <MenuItem onClick={handleMenuClose} sx={{ color: 'error.main' }}>
          Delete
        </MenuItem>
      </Menu>

      {/* Bug Detail Dialog */}
      <Dialog open={showBugDialog} onClose={() => setShowBugDialog(false)} maxWidth="md" fullWidth>
        {selectedBug && (
          <>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" fontWeight={600}>
                {selectedBug.id} - {selectedBug.title}
              </Typography>
              <IconButton onClick={() => setShowBugDialog(false)} size="small">
                <Close />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Description
                  </Typography>
                  <Typography variant="body2">{selectedBug.description}</Typography>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Status
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip
                        icon={getStatusIcon(selectedBug.status)}
                        label={selectedBug.status.replace('-', ' ')}
                        size="small"
                        sx={{
                          bgcolor: getStatusColor(selectedBug.status).bg,
                          color: getStatusColor(selectedBug.status).color,
                          textTransform: 'capitalize',
                        }}
                      />
                    </Box>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Priority
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip
                        label={selectedBug.priority.toUpperCase()}
                        size="small"
                        sx={{
                          bgcolor: getPriorityColor(selectedBug.priority).bg,
                          color: getPriorityColor(selectedBug.priority).color,
                        }}
                      />
                    </Box>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Assignee
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {selectedBug.assignee.name}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Due Date
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {new Date(selectedBug.dueDate).toLocaleDateString()}
                    </Typography>
                  </Box>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Tags
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                    {selectedBug.tags.map((tag, index) => (
                      <Chip key={index} label={tag} size="small" />
                    ))}
                  </Box>
                </Box>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowBugDialog(false)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* New Bug Dialog */}
      <Dialog open={showNewBugDialog} onClose={() => setShowNewBugDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Bug</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Title"
              fullWidth
              value={newBug.title}
              onChange={(e) => setNewBug({ ...newBug, title: e.target.value })}
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={3}
              value={newBug.description}
              onChange={(e) => setNewBug({ ...newBug, description: e.target.value })}
            />
            <FormControl fullWidth>
              <Typography variant="caption" sx={{ mb: 0.5 }}>
                Priority
              </Typography>
              <Select
                value={newBug.priority}
                onChange={(e) => setNewBug({ ...newBug, priority: e.target.value as any })}
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="critical">Critical</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Assignee"
              fullWidth
              value={newBug.assignee}
              onChange={(e) => setNewBug({ ...newBug, assignee: e.target.value })}
            />
            <TextField
              label="Due Date"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={newBug.dueDate}
              onChange={(e) => setNewBug({ ...newBug, dueDate: e.target.value })}
            />
            <TextField
              label="Tags (comma separated)"
              fullWidth
              value={newBug.tags}
              onChange={(e) => setNewBug({ ...newBug, tags: e.target.value })}
              placeholder="e.g., mobile, authentication"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowNewBugDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateBug}
            disabled={!newBug.title || !newBug.description}
          >
            Create Bug
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BugTracker;
